import i18n from "i18next";

const MESSAGE_KEYS = {
  "Invalid username or password": "auth.invalidCredentials",
  "Username already taken": "auth.usernameTaken",
  "Username already exists": "auth.usernameTaken",
  "Email already registered": "auth.emailTaken",
  "Email already exists": "auth.emailTaken",
  "Username or email already exists": "auth.identifierTaken",
  "This username is reserved": "auth.usernameReserved",
  "Incorrect old password": "THE_CURRENT_PASSWORD_IS_INCORRECT",
  "Invalid verification code": "auth.codeInvalid",
  "Invalid or expired verification code": "auth.codeInvalid",
  "No verification code found for this email": "auth.codeInvalid",
  "Verification code has expired": "auth.codeInvalid",
  "Too many failed login attempts. Please try again later.":
    "auth.loginLimited",
  "Too many attempts. Request a new code.": "auth.codeAttemptsLimited",
  "Username already taken and email already registered": "auth.identifierTaken",
  "Username can only contain letters, numbers, dot (.) and underscore (_)":
    "auth.usernameInvalid",
  "Invalid email format": "PLEASE_ENTER_A_VALID_EMAIL",
  "Username or email required": "USER_NAME_OR_EMAIL_REQUIRED",
  "Failed to check username availability": "auth.availabilityFailed",
  "Failed to check availability": "auth.availabilityFailed",
  "Registration failed": "auth.registrationFailed",
  "Unexpected server error during login": "auth.loginFailed",
  "Failed to change password": "settings.password.changeFailed",
  "Verify your email with the code we sent before resetting your password":
    "auth.resetVerificationRequired",
  "Failed to reset password": "auth.passwordResetFailed",
  "Failed to process email": "auth.codeSendFailed",
  "Failed to send verification code": "auth.codeSendFailed",
  "Failed to verify email": "auth.codeVerifyFailed",
  "Google account has no email permission": "auth.googleEmailPermission",
  "Google email is not verified": "auth.googleEmailUnverified",
  "Email already linked to a different Google account":
    "auth.googleEmailLinked",
  "Invalid Google ID token": "auth.googleFailed",
  "Google login failed": "auth.googleFailed",
  "Invalid token": "auth.invalidSession",
  "Invalid request": "common.invalidRequest",
  "Validation error": "common.invalidRequest",
  "Too many requests. Please slow down and try again shortly.":
    "common.tooManyRequests",
  "User not found": "common.userNotFound",
  "User profile not found": "common.userNotFound",
  "Failed to fetch profile": "settings.validation.loadError",
  "Failed to fetch user profile": "settings.validation.loadError",
  "Update failed": "settings.validation.saveError",
  "You can only delete your own account": "settings.validation.deleteOwnOnly",
  "Failed to delete account": "settings.validation.deleteError",
  "Only image files are allowed": "settings.validation.photoType",
  "File content is not a valid image": "settings.validation.photoReadError",
  "File too large": "common.fileTooLarge",
  "Storage backend unavailable": "common.uploadFailed",
  "Failed to upload file to Google Cloud Storage": "common.uploadFailed",
  "Internal server error": "common.errorDescription",
  "username: String should have at least 3 characters": "auth.usernameInvalid",
  "username: String should have at most 50 characters": "auth.usernameInvalid",
  "username: String should match pattern '^[a-zA-Z0-9._]+$'":
    "auth.usernameInvalid",
  "password: String should have at least 8 characters":
    "auth.passwordRequirements",
  "password: String should have at most 128 characters":
    "auth.passwordRequirements",
  "new_password: String should have at least 8 characters":
    "auth.passwordLength",
  "new_password: String should have at most 128 characters":
    "auth.passwordLength",
};

const LOCALIZED_FEEDBACK_KEYS = [
  ...new Set(Object.values(MESSAGE_KEYS)),
  "auth.loginFailed",
  "auth.registrationFailed",
  "auth.googleFailed",
  "auth.codeSendFailed",
  "auth.accountCreateFailed",
  "auth.codeAccepted",
  "auth.codeSent",
  "auth.passwordResetFailed",
  "auth.consentRequired",
  "settings.validation.loadError",
  "settings.validation.saveError",
  "settings.validation.deleteError",
  "settings.validation.photoType",
  "settings.validation.photoReadError",
  "settings.password.changeFailed",
];

// Translate only known application messages. Compiler diagnostics, user text,
// and unknown server details remain intact. Re-resolve stored feedback at render
// time so changing language also updates an error already visible on the page.
export const localizeAuthMessage = (value) => {
  if (typeof value !== "string") return value;
  const message = value.trim();
  if (!i18n.isInitialized) return message;
  const key = MESSAGE_KEYS[message];
  if (key && i18n.exists(key)) return i18n.t(key);
  for (const feedbackKey of LOCALIZED_FEEDBACK_KEYS) {
    for (const language of ["am", "en", "ru"]) {
      const source = i18n.getResource(language, "translation", feedbackKey);
      if (source === message) return i18n.t(feedbackKey);
    }
  }
  return message;
};

const readableDetail = (value) => {
  if (typeof value === "string") return localizeAuthMessage(value);
  if (!Array.isArray(value)) return "";
  return [
    ...new Set(
      value
        .map((item) => {
          const message = typeof item === "string" ? item : item?.msg;
          return typeof message === "string"
            ? localizeAuthMessage(message)
            : "";
        })
        .filter(Boolean),
    ),
  ].join(" ");
};

// FastAPI validation errors carry an array of {loc, msg, type} objects. Keep
// form errors renderable while leaving transport failures to localized copy.
export const normalizeAuthError = (error, fallback) => {
  const data = error?.response?.data;
  return (
    readableDetail(data?.detail) ||
    readableDetail(data?.error) ||
    localizeAuthMessage(fallback)
  );
};
